import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { EPaymentStatus, IPaymentListItem } from "dto/IPayment";
import Button from "components/Button";
import { ENotificationType, notification } from "utils/notification";

const STATUS_LABEL: Record<EPaymentStatus, string> = {
  [EPaymentStatus.Pending]: "Oczekująca",
  [EPaymentStatus.Processing]: "Przetwarzanie",
  [EPaymentStatus.Succeeded]: "Opłacona",
  [EPaymentStatus.Failed]: "Nieudana",
  [EPaymentStatus.Cancelled]: "Anulowana",
  [EPaymentStatus.RequiresAction]: "Wymaga potwierdzenia",
  [EPaymentStatus.Refunded]: "Zwrócona",
  [EPaymentStatus.Disputed]: "Reklamacja (chargeback)",
};

const STATUS_CLASS: Record<EPaymentStatus, string> = {
  [EPaymentStatus.Pending]: styles.pending,
  [EPaymentStatus.Processing]: styles.pending,
  [EPaymentStatus.Succeeded]: styles.ok,
  [EPaymentStatus.Failed]: styles.fail,
  [EPaymentStatus.Cancelled]: styles.fail,
  [EPaymentStatus.RequiresAction]: styles.action,
  [EPaymentStatus.Refunded]: styles.fail,
  [EPaymentStatus.Disputed]: styles.fail,
};

interface Props {
  payment?: IPaymentListItem;
  publishableKey?: string | null;
  /** Zapisana karta profilu — podawana do confirmCardPayment (PI po nieudanym off-session nie ma payment_method). */
  paymentMethodId?: string | null;
  onConfirmed?: () => void;
}

/** Znacznik statusu płatności dla okresu rozliczeniowego (+ potwierdzenie 3DS, gdy wymagane). */
const PaymentStatusBadge = ({
  payment,
  publishableKey,
  paymentMethodId,
  onConfirmed,
}: Props) => {
  const [confirming, setConfirming] = useState(false);

  if (!payment) return <span className={styles.empty}>—</span>;

  const confirm3ds = async (clientSecret: string) => {
    if (!publishableKey) {
      notification({
        type: ENotificationType.ERROR,
        title: "Płatność",
        text: "Brak konfiguracji płatności.",
      });
      return;
    }

    setConfirming(true);
    try {
      const stripe = await loadStripe(publishableKey);
      // confirmCardPayment uruchamia uwierzytelnienie banku (3DS). Podajemy zapisaną kartę jawnie:
      // po nieudanym obciążeniu off-session PaymentIntent jest w requires_payment_method (payment_method=null),
      // więc bez payment_method Stripe zwraca payment_intent_incompatible_payment_method.
      const result = await stripe!.confirmCardPayment(
        clientSecret,
        paymentMethodId ? { payment_method: paymentMethodId } : undefined
      );

      if (result.error) {
        notification({
          type: ENotificationType.ERROR,
          title: "Płatność",
          text: result.error.message ?? "Nie udało się potwierdzić płatności.",
        });
      } else {
        notification({
          type: ENotificationType.SUCCESS,
          title: "Płatność",
          text: "Płatność potwierdzona. Aktywacja nastąpi po zaksięgowaniu.",
        });
      }
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: "Płatność",
        text: "Nie udało się potwierdzić płatności.",
      });
    } finally {
      setConfirming(false);
      onConfirmed?.();
    }
  };

  return (
    <div className={styles.cell}>
      <span className={classNames(styles.badge, STATUS_CLASS[payment.Status])}>
        {STATUS_LABEL[payment.Status]}
      </span>
      {payment.Status === EPaymentStatus.RequiresAction &&
        payment.RequiresActionClientSecret && (
          <Button
            text="Potwierdź"
            loading={confirming}
            onClick={() => confirm3ds(payment.RequiresActionClientSecret!)}
          />
        )}
    </div>
  );
};

export default PaymentStatusBadge;
